import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fileUrl } from "@/lib/api";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

export default function ClickableImage({ path, label, className }) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const start = useRef({ x: 0, y: 0 });

  const reset = useCallback(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, []);

  if (!path) return null;
  const src = fileUrl(path);

  const clamp = (s) => Math.min(5, Math.max(1, s));
  const zoomBy = (delta) =>
    setScale((s) => {
      const ns = clamp(s + delta);
      if (ns === 1) setPos({ x: 0, y: 0 });
      return ns;
    });

  const onWheel = (e) => {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 0.3 : -0.3);
  };
  const onDown = (e) => {
    if (scale <= 1) return;
    dragging.current = true;
    const pt = e.touches ? e.touches[0] : e;
    start.current = { x: pt.clientX - pos.x, y: pt.clientY - pos.y };
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const pt = e.touches ? e.touches[0] : e;
    setPos({ x: pt.clientX - start.current.x, y: pt.clientY - start.current.y });
  };
  const onUp = () => (dragging.current = false);

  return (
    <>
      <img
        src={src}
        alt={label}
        data-testid="viewable-image"
        onClick={() => { reset(); setOpen(true); }}
        className={`cursor-zoom-in ${className || ""}`}
      />
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="bg-[#0a0a0a] border-zinc-800 max-w-4xl p-2">
          <DialogTitle className="sr-only">{label || "Xem ảnh"}</DialogTitle>
          <DialogDescription className="sr-only">Ảnh phóng to — cuộn hoặc dùng nút để zoom, kéo để di chuyển.</DialogDescription>
          <div
            className="relative w-full h-[70vh] overflow-hidden bg-black rounded-sm select-none"
            onWheel={onWheel}
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onDown}
            onTouchMove={onMove}
            onTouchEnd={onUp}
          >
            <img
              src={src}
              alt={label}
              draggable={false}
              data-testid="zoom-image"
              className="absolute inset-0 m-auto max-w-full max-h-full object-contain transition-transform duration-75"
              style={{
                transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                cursor: scale > 1 ? (dragging.current ? "grabbing" : "grab") : "default",
              }}
            />
          </div>
          <div className="flex items-center justify-center gap-2 pt-2">
            <button data-testid="zoom-out" onClick={() => zoomBy(-0.5)} className="p-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary rounded-sm transition-colors">
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-zinc-500 w-12 text-center">{Math.round(scale * 100)}%</span>
            <button data-testid="zoom-in" onClick={() => zoomBy(0.5)} className="p-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary rounded-sm transition-colors">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button data-testid="zoom-reset" onClick={reset} className="p-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary rounded-sm transition-colors">
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
          {label && <div className="text-center text-zinc-400 text-sm pb-1">{label}</div>}
        </DialogContent>
      </Dialog>
    </>
  );
}
